import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bot, Search, X, HelpCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map each Step 3 section onto the Help category used to prioritise suggestions.
const SECTION_TO_CATEGORY = {
  org: "Org",
  inbound: "Inbound",
  outbound: "Outbound",
  inventory: "Inventory",
  automation: "Automation",
  integration: "Integration",
  reporting: "Reporting",
  pain_points: "PainPoints",
};

const MAX_SUGGESTIONS = 5;

function normalise(s) {
  return (s || "").trim().toLowerCase();
}

/** Exact term → exact keyword → substring fallback. */
function findMatch(terms, query) {
  const q = normalise(query);
  if (!q) return null;
  const exact = terms.find((t) => normalise(t.term) === q);
  if (exact) return exact;
  const kw = terms.find((t) => (t.keywords || []).some((k) => normalise(k) === q));
  if (kw) return kw;
  return terms.find((t) => {
    if (normalise(t.term).includes(q)) return true;
    return (t.keywords || []).some((k) => normalise(k).includes(q) || q.includes(normalise(k)));
  }) || null;
}

/**
 * Compact Joule-style SAP Help.
 * Renders as a small floating icon (bottom-right). Click opens a right-side
 * drawer with the same controlled-knowledge-base logic — no chatbot framing.
 */
export function HelpAssistantPanel({ activeSectionId, assessmentType = "Warehouse", activeQuestionId = "", sectionQuestionIds = [] }) {
  const [terms, setTerms] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/help-knowledge`)
      .then((r) => { if (!cancelled) setTerms(r.data || []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  // Close drawer on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Only ever surface entries for the current Assessment Type + Common. This
  // keeps Cloud Migration free of unrelated Warehouse terms (and vice versa).
  const baseTerms = useMemo(() => {
    const at = assessmentType || "Warehouse";
    return terms.filter((t) => {
      const tat = t.assessment_type || "Warehouse";
      return tat === at || tat === "Common";
    });
  }, [terms, assessmentType]);

  // Question-aware suggestions with the confirmed fallback order:
  // 1) mapped to the active question's field_key
  // 2) mapped to any question in the current section
  // 3) most relevant terms for the Assessment Type (section-category priority for Warehouse)
  const { suggestions, suggestionScope } = useMemo(() => {
    if (activeQuestionId) {
      const mapped = baseTerms.filter((t) => (t.related_question_ids || []).includes(activeQuestionId));
      if (mapped.length) return { suggestions: mapped.slice(0, MAX_SUGGESTIONS), suggestionScope: "question" };
    }
    if (sectionQuestionIds && sectionQuestionIds.length) {
      const secMapped = baseTerms.filter((t) =>
        (t.related_question_ids || []).some((id) => sectionQuestionIds.includes(id)));
      if (secMapped.length) return { suggestions: secMapped.slice(0, MAX_SUGGESTIONS), suggestionScope: "section" };
    }
    const priorityCat = SECTION_TO_CATEGORY[activeSectionId];
    const prioritised = baseTerms.slice().sort((a, b) => {
      const aPrio = a.category === priorityCat ? 0 : 1;
      const bPrio = b.category === priorityCat ? 0 : 1;
      if (aPrio !== bPrio) return aPrio - bPrio;
      return (a.term || "").localeCompare(b.term || "");
    });
    return { suggestions: prioritised.slice(0, MAX_SUGGESTIONS), suggestionScope: "type" };
  }, [baseTerms, activeQuestionId, sectionQuestionIds, activeSectionId]);

  const hasMappedForActive = suggestionScope === "question";

  const pickTerm = (t) => {
    setSelected(t);
    setNotFound(false);
    setQuery(t.term);
  };

  const runSearch = () => {
    const match = findMatch(baseTerms, query);
    if (match) {
      setSelected(match);
      setNotFound(false);
    } else {
      setSelected(null);
      setNotFound(true);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); runSearch(); }
  };

  const clear = () => { setSelected(null); setQuery(""); setNotFound(false); };

  return (
    <>
      {/* Floating Ask PRIVA assistant — anchored bottom-right, always available */}
      <button
        data-testid="help-assistant-fab"
        onClick={() => setOpen(true)}
        title="Ask PRIVA"
        aria-label="Ask PRIVA"
        className={`fixed bottom-24 right-6 z-40 group flex items-center gap-2 pl-3 pr-4 py-2.5 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-full shadow-lg shadow-[#6E5BCE]/30 transition-all ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <Bot className="w-4 h-4" strokeWidth={1.75} />
        <span className="text-xs font-medium">Ask PRIVA</span>
        {hasMappedForActive && (
          <span
            data-testid="help-assistant-badge"
            title="Help available for this question"
            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#F59E0B] border-2 border-white shadow"
          />
        )}
      </button>

      {/* Drawer + backdrop */}
      {open && (
        <>
          <div
            data-testid="help-assistant-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/20 z-40"
          />
          <aside
            data-testid="help-assistant-drawer"
            className="fixed top-0 right-0 h-full w-[360px] max-w-full z-50 bg-white border-l border-[#E5E7EB] shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-gradient-to-br from-[#F0EDF9] to-white">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#6E5BCE] flex items-center justify-center shadow-sm">
                  <Bot className="w-3.5 h-3.5 text-white" strokeWidth={2} />
                </div>
                <div>
                  <p
                    className="text-[12px] font-semibold text-[#111827] tracking-wide"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    Ask PRIVA
                  </p>
                  <p className="text-[10px] text-[#6B7280]">Quick clarity for key terms in your evaluation</p>
                </div>
              </div>
              <button
                data-testid="help-assistant-close"
                onClick={() => setOpen(false)}
                className="text-[#6B7280] hover:text-[#111827] p-1 rounded-sm hover:bg-[#F3F4F6]"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search input */}
            <div className="px-4 py-3 border-b border-[#E5E7EB]">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                <input
                  data-testid="help-assistant-input"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setNotFound(false); }}
                  onKeyDown={onKeyDown}
                  placeholder="Ask about a term, e.g. Handling Unit"
                  autoFocus
                  className="w-full pl-8 pr-14 py-2 text-xs border border-[#E5E7EB] rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#6E5BCE] focus:border-[#6E5BCE]"
                />
                {query && (
                  <button
                    onClick={clear}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827] text-[10px]"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {/* Answer card */}
              {selected && (
                <div
                  key={selected.id}
                  data-testid="help-assistant-answer"
                  className="border border-[#DDD6FE] bg-[#FAFAFF] rounded-sm p-3 mb-3"
                >
                  <p
                    className="text-[13px] font-semibold text-[#111827] mb-1.5"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    {selected.term}
                  </p>
                  <p className="text-[12px] text-[#1F2937] leading-relaxed">
                    {selected.definition}
                  </p>
                  {selected.why_it_matters && (
                    <div className="mt-2 pt-2 border-t border-[#E5E7EB]">
                      <p className="text-[9px] font-semibold text-[#6E5BCE] uppercase tracking-wider mb-0.5">
                        Why it matters
                      </p>
                      <p className="text-[11px] text-[#374151] leading-relaxed">
                        {selected.why_it_matters}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Not-found fallback */}
              {notFound && (
                <div
                  data-testid="help-assistant-notfound"
                  className="mb-3 flex items-start gap-2 px-3 py-2.5 bg-[#FFFBEB] border border-[#FCD34D]/50 rounded-sm"
                >
                  <HelpCircle className="w-3.5 h-3.5 text-[#B45309] mt-0.5 shrink-0" strokeWidth={1.75} />
                  <p className="text-[11px] text-[#78350F] leading-relaxed">
                    I don't have a defined help entry for that term yet. Try a related SAP warehouse term or contact your PrimeS4 consultant.
                  </p>
                </div>
              )}

              {/* Suggested terms */}
              {suggestions.length > 0 && (
                <div data-testid="help-assistant-suggestions">
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">
                    {selected
                      ? "Other terms"
                      : suggestionScope === "question"
                      ? "For this question"
                      : suggestionScope === "section"
                      ? "Relevant for this step"
                      : "Relevant terms"}
                  </p>
                  <ul className="space-y-1">
                    {suggestions
                      .filter((t) => !selected || t.id !== selected.id)
                      .slice(0, MAX_SUGGESTIONS)
                      .map((t) => (
                        <li key={t.id}>
                          <button
                            data-testid={`help-suggestion-${t.id}`}
                            onClick={() => pickTerm(t)}
                            className="w-full text-left px-2.5 py-1.5 rounded-sm text-[11px] text-[#374151] hover:bg-[#F0EDF9] hover:text-[#6E5BCE] transition-colors border border-transparent hover:border-[#DDD6FE]"
                          >
                            {t.term}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-[#E5E7EB] bg-[#FAFAFF]">
              <p className="text-[10px] text-[#6E5BCE] font-semibold tracking-wide">
                Powered by PRIVA
              </p>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                Part of SmartEvaluate. Answers come from your consulting team.
              </p>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
